import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { 
  Trophy, ArrowLeft, ArrowRight, User, Users, FileText, 
  CreditCard, CheckCircle, MapPin, Phone, Mail,
  Calendar, Loader2, Hash, RefreshCw
} from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';
import { Progress } from '../components/ui/progress';
import BrandHeader from '../components/BrandHeader';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { id: 1, title: 'Child Details', icon: User },
  { id: 2, title: 'Parent Details', icon: Users },
  { id: 3, title: 'Rules & Consent', icon: FileText },
  { id: 4, title: 'Payment', icon: CreditCard },
  { id: 5, title: 'Confirmation', icon: CheckCircle }
];

const RegistrationPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [registrationId, setRegistrationId] = useState(null);
  const [registrationData, setRegistrationData] = useState(null);
  const [eventFee, setEventFee] = useState({ fee: 10.0, currency: 'usd', event_name: 'Brain Gym Olympiad' });

  // Form data
  const [childData, setChildData] = useState({
    full_name: '',
    age: '',
    school_name: '',
    city: ''
  });

  const [parentData, setParentData] = useState({
    full_name: '',
    phone_number: '',
    email: ''
  });

  const [consentGiven, setConsentGiven] = useState(false);
  const [username, setUsername] = useState('');
  const [usernameLoading, setUsernameLoading] = useState(false);
  const [competitionAgeGroup, setCompetitionAgeGroup] = useState('');

  // Free-access code
  const [accessCode, setAccessCode] = useState('');
  const [codeValidating, setCodeValidating] = useState(false);
  const [codeStatus, setCodeStatus] = useState(null); // { valid, label, uses_remaining } | { error }

  // Load registration ID from URL if returning from payment
  useEffect(() => {
    const regId = searchParams.get('registration_id');
    if (regId) {
      setRegistrationId(regId);
      setCurrentStep(4);
      loadRegistration(regId);
    }
  }, [searchParams]);

  // Load event fee and competition age group
  useEffect(() => {
    const loadFee = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/event-fee`);
        setEventFee(response.data);
      } catch (err) {
        console.error('Failed to load event fee:', err);
      }
    };
    const loadAgeGroup = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/registration/status`);
        if (res.data.competition?.ageGroup) {
          setCompetitionAgeGroup(res.data.competition.ageGroup);
        }
      } catch (err) {}
    };
    loadFee();
    loadAgeGroup();
  }, []);

  const loadRegistration = async (id) => {
    try {
      const response = await axios.get(`${API_URL}/api/registration/by-id/${id}`);
      setRegistrationData(response.data);
    } catch (err) {
      console.error('Failed to load registration:', err);
    }
  };

  const suggestUsername = async (name) => {
    if (!name || name.trim().length < 2) return;
    setUsernameLoading(true);
    try {
      const firstName = name.trim().split(' ')[0];
      const res = await axios.get(`${API_URL}/api/registration/suggest-username/${encodeURIComponent(firstName)}`);
      setUsername(res.data.username);
    } catch (err) {
      console.error('Failed to suggest username:', err);
    } finally {
      setUsernameLoading(false);
    }
  };

  const handleChildChange = (field, value) => {
    setChildData(prev => ({ ...prev, [field]: value }));
    // Auto-suggest username when name changes
    if (field === 'full_name' && value.trim().length >= 2 && !username) {
      suggestUsername(value);
    }
  };

  const handleParentChange = (field, value) => {
    setParentData(prev => ({ ...prev, [field]: value }));
  };

  const validateStep = () => {
    setError('');
    
    if (currentStep === 1) {
      if (!childData.full_name || !childData.age || !childData.city) {
        setError('Please fill in all fields');
        return false;
      }
      if (isNaN(childData.age) || childData.age < 3 || childData.age > 18) {
        setError('Please enter a valid age between 3 and 18');
        return false;
      }
      // Validate against competition age group
      if (competitionAgeGroup) {
        const nums = competitionAgeGroup.match(/\d+/g);
        if (nums && nums.length >= 2) {
          const minAge = parseInt(nums[0]);
          const maxAge = parseInt(nums[nums.length - 1]);
          const age = parseInt(childData.age);
          if (age < minAge || age > maxAge) {
            setError(`This competition is for ages ${minAge}-${maxAge} only. Your child's age (${age}) is outside the allowed range.`);
            return false;
          }
        }
      }
    }

    if (currentStep === 2) {
      // Guardian name is only mandatory for minors (< 16). For 16+, adults can
      // register themselves and provide their own contact info without a guardian.
      const ageNum = parseInt(childData.age) || 0;
      const guardianRequired = ageNum > 0 && ageNum < 16;
      if (guardianRequired && !parentData.full_name) {
        setError('Guardian name is required for participants under 16');
        return false;
      }
      if (!parentData.phone_number || !parentData.email) {
        setError('Please fill in all fields');
        return false;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(parentData.email)) {
        setError('Please enter a valid email address');
        return false;
      }
    }

    if (currentStep === 3) {
      if (!consentGiven) {
        setError('You must provide consent to continue');
        return false;
      }
    }

    return true;
  };

  const handleNext = async () => {
    if (!validateStep()) return;

    if (currentStep === 3) {
      // Go directly to payment — no registration saved yet
      setCurrentStep(4);
    } else {
      setCurrentStep(prev => Math.min(prev + 1, 5));
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    setError('');
  };

  const handlePayment = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API_URL}/api/payments/create-checkout`, {
        origin_url: window.location.origin,
        child_name: childData.full_name,
        child_age: parseInt(childData.age),
        child_school: childData.school_name,
        child_city: childData.city,
        parent_name: parentData.full_name,
        parent_phone: parentData.phone_number,
        parent_email: parentData.email,
        consent_given: consentGiven,
        username: username
      });

      // Redirect to Stripe
      window.location.href = response.data.url;
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Payment initialization failed');
      setLoading(false);
    }
  };

  const validateAccessCode = async () => {
    setCodeStatus(null);
    const trimmed = accessCode.trim().toUpperCase();
    if (!trimmed) return;
    setCodeValidating(true);
    try {
      const res = await axios.post(`${API_URL}/api/registration/validate-code`, { code: trimmed });
      setCodeStatus(res.data);
    } catch (err) {
      setCodeStatus({ error: err.response?.data?.detail || 'Invalid code' });
    } finally {
      setCodeValidating(false);
    }
  };

  const handleFreeRegistration = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API_URL}/api/registration/free-with-code`, {
        child: {
          full_name: childData.full_name,
          age: parseInt(childData.age),
          school_name: childData.school_name,
          city: childData.city,
        },
        parent: {
          full_name: parentData.full_name,
          phone_number: parentData.phone_number,
          email: parentData.email,
        },
        consent_given: consentGiven,
        username: username,
        access_code: accessCode.trim().toUpperCase(),
      });
      // Navigate to success page with the codes
      const params = new URLSearchParams({
        participant_id: res.data.participant_id,
        event_code: res.data.event_code,
        parent_access_code: res.data.parent_access_code,
        child_name: res.data.child_name,
        parent_email: res.data.parent_email,
        username: res.data.username || '',
        free: 'true',
      });
      navigate(`/registration/success?${params.toString()}`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Free registration failed');
      setLoading(false);
    }
  };

  const renderStepIndicator = () => (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-4">
        {STEPS.map((step, index) => {
          const Icon = step.icon;
          const isActive = currentStep === step.id;
          const isCompleted = currentStep > step.id;

          return (
            <React.Fragment key={step.id}>
              <div className="flex flex-col items-center">
                <div
                  className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-all ${
                    isActive
                      ? 'bg-[#2565eb] text-white shadow-[0_4px_0_#1e3a8a]'
                      : isCompleted
                      ? 'bg-[#22c55e] text-white shadow-[0_4px_0_#16a34a]'
                      : 'bg-[#e2e8f0] text-[#64748b]'
                  }`}
                >
                  {isCompleted ? (
                    <CheckCircle className="w-6 h-6" />
                  ) : (
                    <Icon className="w-6 h-6" />
                  )}
                </div>
                <span className={`text-xs font-semibold mt-2 hidden sm:block ${
                  isActive ? 'text-[#2565eb]' : 'text-[#64748b]'
                }`}>
                  {step.title}
                </span>
              </div>
              {index < STEPS.length - 1 && (
                <div className={`flex-1 h-1 mx-2 rounded-full ${
                  currentStep > step.id ? 'bg-[#22c55e]' : 'bg-[#e2e8f0]'
                }`} />
              )}
            </React.Fragment>
          );
        })}
      </div>
      <Progress value={(currentStep / 5) * 100} className="h-2" />
    </div>
  );

  const renderStep1 = () => (
    <div className="space-y-5 animate-fade-in-up">
      <h2 className="text-2xl font-bold text-[#0f172a]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
        Tell us about the participant
      </h2>

      <div>
        <label className="block text-sm font-semibold text-[#475569] mb-2">Full Name</label>
        <div className="relative">
          <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748b]" />
          <input
            type="text"
            value={childData.full_name}
            onChange={(e) => handleChildChange('full_name', e.target.value)}
            className="input-olympiad pl-12"
            placeholder="Enter child's full name"
            data-testid="child-name-input"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-semibold text-[#475569] mb-2">Username</label>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Hash className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748b]" />
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9]/g, ''))}
              className="input-olympiad pl-12"
              placeholder="Auto-suggested username"
              data-testid="username-input"
            />
          </div>
          <button
            type="button"
            onClick={() => suggestUsername(childData.full_name)}
            disabled={usernameLoading || !childData.full_name}
            className="px-4 py-3 rounded-2xl font-bold border-2 border-[#2565eb] text-[#2565eb] hover:bg-[#eff6ff] transition-all disabled:opacity-50 flex items-center gap-1 text-sm"
            data-testid="refresh-username-btn"
          >
            {usernameLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </button>
        </div>
        <p className="text-xs text-[#94a3b8] mt-1">This will be your child's display name in the competition</p>
      </div>

      <div>
        <label className="block text-sm font-semibold text-[#475569] mb-2">Age</label>
        <div className="relative">
          <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748b]" />
          <input
            type="number"
            value={childData.age}
            onChange={(e) => handleChildChange('age', e.target.value)}
            className="input-olympiad pl-12"
            placeholder="Age"
            min="3"
            max="18"
            data-testid="child-age-input"
          />
        </div>
        {competitionAgeGroup && (
          <p className="text-xs text-[#2565eb] mt-1 font-medium" data-testid="age-group-hint">This competition is for ages {competitionAgeGroup}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-semibold text-[#475569] mb-2">City</label>
        <div className="relative">
          <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748b]" />
          <input
            type="text"
            value={childData.city}
            onChange={(e) => handleChildChange('city', e.target.value)}
            className="input-olympiad pl-12"
            placeholder="Enter city"
            data-testid="child-city-input"
          />
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => {
    // Guardian name is mandatory only for minors (< 16); adults registering
    // themselves may leave it blank. Age is captured on Step 1.
    const ageNum = parseInt(childData.age) || 0;
    const guardianRequired = ageNum > 0 && ageNum < 16;
    return (
    <div className="space-y-5 animate-fade-in-up">
      <h2 className="text-2xl font-bold text-[#0f172a]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
        Parent/Guardian Details
      </h2>
      <p className="text-[#64748b]">We'll send confirmation and updates to this contact</p>

      <div>
        <label className="block text-sm font-semibold text-[#475569] mb-2">
          Full Name
          {guardianRequired
            ? <span className="text-[#dc2626] ml-1">*</span>
            : <span className="text-[#94a3b8] font-normal ml-1">(optional)</span>}
        </label>
        <div className="relative">
          <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748b]" />
          <input
            type="text"
            value={parentData.full_name}
            onChange={(e) => handleParentChange('full_name', e.target.value)}
            className="input-olympiad pl-12"
            placeholder={guardianRequired ? "Parent or guardian's full name" : "Optional — leave blank if not applicable"}
            data-testid="parent-name-input"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-semibold text-[#475569] mb-2">Phone Number</label>
        <div className="relative">
          <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748b]" />
          <input
            type="tel"
            value={parentData.phone_number}
            onChange={(e) => handleParentChange('phone_number', e.target.value)}
            className="input-olympiad pl-12"
            placeholder="Enter phone number"
            data-testid="parent-phone-input"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-semibold text-[#475569] mb-2">Email Address</label>
        <div className="relative">
          <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748b]" />
          <input
            type="email"
            value={parentData.email}
            onChange={(e) => handleParentChange('email', e.target.value)}
            className="input-olympiad pl-12"
            placeholder="Enter email address"
            data-testid="parent-email-input"
          />
        </div>
      </div>
    </div>
    );
  };

  const renderStep3 = () => (
    <div className="space-y-6 animate-fade-in-up">
      <h2 className="text-2xl font-bold text-[#0f172a]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
        Event Rules & Consent
      </h2>

      <div className="bg-[#eff6ff] rounded-2xl p-6 border-2 border-[#bfdbfe]">
        <h3 className="font-bold text-[#0f172a] mb-4">Competition Rules</h3>
        <ul className="space-y-3 text-sm text-[#475569]">
          <li className="flex items-start gap-2">
            <CheckCircle className="w-5 h-5 text-[#2565eb] flex-shrink-0 mt-0.5" />
            <span>The competition consists of 8 rounds with timed questions</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle className="w-5 h-5 text-[#2565eb] flex-shrink-0 mt-0.5" />
            <span>Points are awarded for correct answers, with bonuses for speed and streaks</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle className="w-5 h-5 text-[#2565eb] flex-shrink-0 mt-0.5" />
            <span>A live leaderboard will show rankings during the event</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle className="w-5 h-5 text-[#2565eb] flex-shrink-0 mt-0.5" />
            <span>Certificates will be provided to all participants after the event</span>
          </li>
        </ul>
      </div>

      <div className="card-olympiad p-6" data-testid="consent-section">
        <div className="flex items-start gap-4">
          <Checkbox
            id="consent"
            checked={consentGiven}
            onCheckedChange={(checked) => setConsentGiven(checked)}
            className="mt-1 h-6 w-6 rounded-lg border-2 border-[#2565eb] data-[state=checked]:bg-[#2565eb]"
            data-testid="consent-checkbox"
          />
          <label htmlFor="consent" className="text-[#475569] cursor-pointer">
            <span className="font-semibold text-[#0f172a]">Leaderboard &amp; Public Display Consent</span>
            <br />
            I consent (or give parental consent) for the participant&apos;s first name and last initial to be displayed on the event leaderboard.
          </label>
        </div>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-6 animate-fade-in-up">
      <h2 className="text-2xl font-bold text-[#0f172a]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
        Complete Payment
      </h2>
      <p className="text-[#64748b]">Secure your spot in the {eventFee.event_name}</p>

      {/* Access Code Section — free registration bypass */}
      <div className="card-olympiad p-6 border-2 border-[#dbeafe]" data-testid="access-code-section">
        <h3 className="text-base font-bold text-[#0f172a] mb-2 flex items-center gap-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
          <Hash className="w-4 h-4 text-[#2565eb]" /> Have a free-access code?
        </h3>
        <p className="text-xs text-[#64748b] mb-4">If your school or sponsor gave you a code (looks like <span className="font-mono">BRAINGYM-XXXXXX</span>), enter it here to register for free.</p>
        <div className="flex gap-2">
          <input
            type="text"
            value={accessCode}
            onChange={(e) => { setAccessCode(e.target.value.toUpperCase()); setCodeStatus(null); }}
            placeholder="BRAINGYM-XXXXXX"
            className="input-olympiad font-mono uppercase flex-1"
            data-testid="access-code-input"
            disabled={loading}
          />
          <button
            onClick={validateAccessCode}
            disabled={!accessCode.trim() || codeValidating || loading}
            className="px-4 py-2.5 rounded-xl font-bold border-2 border-[#2565eb] text-[#2565eb] hover:bg-[#eff6ff] text-sm transition-all disabled:opacity-50"
            data-testid="access-code-check-btn"
          >
            {codeValidating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Check'}
          </button>
        </div>
        {codeStatus?.valid && (
          <div className="mt-3 p-3 bg-[#f0fdf4] border border-[#bbf7d0] rounded-xl text-sm text-[#16a34a] flex items-center gap-2" data-testid="access-code-valid">
            <CheckCircle className="w-4 h-4" />
            <span>Valid code{codeStatus.label ? ` (${codeStatus.label})` : ''} — registration will be free.</span>
          </div>
        )}
        {codeStatus?.error && (
          <div className="mt-3 p-3 bg-[#fef2f2] border border-[#fecaca] rounded-xl text-sm text-[#dc2626]" data-testid="access-code-error">
            {codeStatus.error}
          </div>
        )}
        {codeStatus?.valid && (
          <button
            onClick={handleFreeRegistration}
            disabled={loading}
            className="mt-4 w-full px-5 py-3 rounded-xl font-bold bg-[#22c55e] text-white hover:bg-[#16a34a] transition-all flex items-center justify-center gap-2 disabled:opacity-50 shadow-[0_4px_0_#15803d]"
            data-testid="register-free-btn"
          >
            {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> Registering...</> : <><CheckCircle className="w-5 h-5" /> Register Free with Code</>}
          </button>
        )}
      </div>

      <div className="card-olympiad p-8 text-center">
        <div className="w-20 h-20 bg-[#face39] rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-[0_6px_0_#d97706]">
          <CreditCard className="w-10 h-10 text-[#0f172a]" />
        </div>

        <h3 className="text-3xl font-bold text-[#0f172a] mb-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
          ${eventFee.fee.toFixed(2)} {eventFee.currency.toUpperCase()}
        </h3>
        <p className="text-[#64748b] mb-6">Participation Fee</p>

        {registrationData && (
          <div className="bg-[#f8fafc] rounded-2xl p-4 mb-6 text-left">
            <p className="text-sm text-[#64748b]">Participant: <span className="font-semibold text-[#0f172a]">{registrationData.child_name}</span></p>
            <p className="text-sm text-[#64748b]">Participant ID: <span className="font-semibold text-[#0f172a]">{registrationData.participant_id}</span></p>
          </div>
        )}

        <button
          onClick={handlePayment}
          disabled={loading}
          className="btn-secondary w-full flex items-center justify-center gap-2"
          data-testid="pay-now-button"
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Processing...
            </>
          ) : (
            <>
              <CreditCard className="w-5 h-5" />
              Pay Now
            </>
          )}
        </button>

        <p className="text-xs text-[#64748b] mt-4">
          Secure payment powered by Stripe
        </p>
      </div>
    </div>
  );

  const renderStep5 = () => (
    <div className="text-center space-y-6 animate-fade-in-up">
      <div className="w-24 h-24 bg-[#22c55e] rounded-3xl flex items-center justify-center mx-auto shadow-[0_6px_0_#16a34a]">
        <CheckCircle className="w-14 h-14 text-white" />
      </div>

      <h2 className="text-3xl font-bold text-[#0f172a]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
        Registration Complete!
      </h2>
      <p className="text-[#64748b] max-w-md mx-auto">
        We've sent a confirmation email to <span className="font-semibold">{registrationData?.parent_email}</span> with all the details.
      </p>

      {registrationData && (
        <div className="card-olympiad p-6 text-left max-w-md mx-auto">
          <h3 className="font-bold text-[#0f172a] mb-4">Your Access Codes</h3>
          <div className="space-y-3">
            <div className="bg-[#f8fafc] rounded-xl p-3">
              <p className="text-xs text-[#64748b]">Participant ID</p>
              <p className="font-bold text-[#0f172a] font-mono">{registrationData.participant_id}</p>
            </div>
            <div className="bg-[#f8fafc] rounded-xl p-3">
              <p className="text-xs text-[#64748b]">Event Code</p>
              <p className="font-bold text-[#2565eb] font-mono">{registrationData.event_code}</p>
            </div>
            <div className="bg-[#f8fafc] rounded-xl p-3">
              <p className="text-xs text-[#64748b]">Parent Access Code</p>
              <p className="font-bold text-[#2565eb] font-mono">{registrationData.parent_access_code}</p>
            </div>
          </div>

          <div className="mt-4 p-4 bg-[#fef3c7] rounded-xl border-2 border-[#f59e0b]">
            <p className="text-sm text-[#92400e]">
              <strong>Note:</strong> Your event code and parent access code will activate when the host starts the event. Your practice zone is available immediately.
            </p>
          </div>
        </div>
      )}

      <div className="flex gap-4 justify-center pt-4">
        <button
          onClick={() => navigate('/practice')}
          className="btn-primary flex items-center gap-2"
          data-testid="go-to-practice-button"
        >
          Go to Practice Zone
          <ArrowRight className="w-5 h-5" />
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#fdfdfc] flex flex-col">
      <BrandHeader />

      {/* Main Content */}
      <main className="flex-1 p-6 max-w-2xl mx-auto w-full">
        {currentStep < 5 && renderStepIndicator()}

        {error && (
          <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-4 mb-6 text-red-600 text-sm" data-testid="registration-error">
            {error}
          </div>
        )}

        <div className="card-olympiad p-8">
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
          {currentStep === 4 && renderStep4()}
          {currentStep === 5 && renderStep5()}

          {/* Navigation Buttons */}
          {currentStep < 4 && (
            <div className="flex justify-between mt-8 pt-6 border-t border-[#e2e8f0]">
              {currentStep > 1 ? (
                <button
                  onClick={handleBack}
                  className="flex items-center gap-2 text-[#64748b] hover:text-[#0f172a] font-semibold transition-colors"
                  data-testid="back-button"
                >
                  <ArrowLeft className="w-5 h-5" />
                  Back
                </button>
              ) : (
                <div />
              )}
              <button
                onClick={handleNext}
                disabled={loading}
                className="btn-primary flex items-center gap-2 max-w-full"
                data-testid="next-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    {currentStep === 3 ? (
                      <>
                        {/* Shorter label on mobile to keep the button compact and tidy. */}
                        <span className="sm:hidden">Continue to Payment</span>
                        <span className="hidden sm:inline">Submit &amp; Continue to Payment</span>
                      </>
                    ) : (
                      'Continue'
                    )}
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default RegistrationPage;
